import { Box, Stack, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import type { Completude } from '../../services/efetivosService';
import { contagemDaCompletude, faixaDaCompletude, porcentagemDe, type FaixaDaCompletude } from './completude';

/**
 * A barra pinta a faixa; o texto usa o tom escuro da mesma cor, para ler com contraste de pelo
 * menos 4,5:1 sobre o branco — o verde-claro da barra não serviria de texto.
 */
const CORES_DA_FAIXA: Record<FaixaDaCompletude, { barra: string; texto: string }> = {
  baixa: { barra: 'error.dark', texto: 'error.dark' },
  media: { barra: 'warning.dark', texto: 'warning.dark' },
  alta: { barra: 'success.light', texto: 'success.dark' },
  completa: { barra: 'success.main', texto: 'success.dark' },
};

/** A dica nasce ancorada na barra e, perto da borda da tela, guarda 8px de respiro dela. */
const AFASTA_DA_BORDA = [{ name: 'preventOverflow', options: { padding: 8 } }];

interface BarraDeCompletudeProps {
  completude: Completude | undefined;
  /** Nas janelas: a contagem por extenso ao lado da barra, além da dica. */
  comContagem?: boolean;
}

/**
 * Quanto do que se aplica ao item já está preenchido (#606, E1/E4): uma barra de 90 × 6 px na cor
 * da faixa e a porcentagem escrita ao lado — quem não distingue as cores lê o mesmo. A dica (ao
 * passar o mouse) diz só quantas estão preenchidas ("4 de 10 informações preenchidas"), sem listar
 * o que falta — isso fica dentro da janela; o leitor de tela ouve o mesmo no nome da barra, que
 * não é parada de Tab — numa listagem seria uma parada a mais em cada linha. Total zero é "Não se
 * aplica"; sem o dado, nada aparece.
 */
export function BarraDeCompletude({ completude, comContagem = false }: BarraDeCompletudeProps) {
  if (!completude) return null;
  if (completude.total === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Não se aplica
      </Typography>
    );
  }
  const porcentagem = porcentagemDe(completude);
  const faixa = faixaDaCompletude(porcentagem);
  const cores = CORES_DA_FAIXA[faixa];
  const contagem = contagemDaCompletude(completude);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {comContagem && (
        <Typography variant="body2" color="text.secondary">
          {contagem}
        </Typography>
      )}
      <Tooltip title={contagem} placement="bottom-start" slotProps={{ popper: { modifiers: AFASTA_DA_BORDA } }}>
        <Box
          role="progressbar"
          aria-label={contagem}
          aria-valuenow={porcentagem}
          aria-valuemin={0}
          aria-valuemax={100}
          data-faixa={faixa}
          sx={{
            width: 90,
            height: 6,
            borderRadius: 3,
            bgcolor: 'grey.200',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <Box sx={{ width: `${porcentagem}%`, height: '100%', bgcolor: cores.barra, borderRadius: 3 }} />
        </Box>
      </Tooltip>
      <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: cores.texto }}>
        <Typography variant="body2" fontWeight={600} color="inherit">
          {`${porcentagem}%`}
        </Typography>
        {faixa === 'completa' && <CheckCircleIcon data-testid="completude-concluida" sx={{ fontSize: 16 }} aria-hidden />}
      </Stack>
    </Stack>
  );
}
